import { useEffect, useRef, useState } from 'react';
import { Card, Stat, StatusBadge, EmptyState, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi } from '@/shared/api/superApi';

interface HealthData {
  status: 'healthy' | 'degraded' | 'down';
  checked_at: string;
  asterisk: { ok: boolean; status?: number; latency_ms?: number | null; body?: unknown };
  database: { ok: boolean; latency_ms?: number | null; driver?: string | null; error?: string };
  active_channels: number;
  calls: { active: number; inbound: number; outbound: number; last_hour: number; avg_talk_sec: number };
  extensions: { registered: number; provisioned: number };
  trunks: { total: number; online: number; failed: number; items: { id: number; name: string; status: string }[] };
  nodes: { id: string; role: string; host?: string; status: string }[];
}

const STATUS_LABEL: Record<string, string> = { healthy: 'תקין', degraded: 'מוגבל', down: 'תקלה' };
const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger'> = { healthy: 'ok', degraded: 'warn', down: 'danger' };

export function Health() {
  const [data, setData] = useState<HealthData | null>(null);
  const [firstLoadFailed, setFirstLoadFailed] = useState(false);
  // Seconds since the last SUCCESSFUL poll — surfaced so a frozen "תקין" during
  // an outage isn't silently trusted. This is THE incident screen.
  const [staleSec, setStaleSec] = useState(0);
  const lastOk = useRef<number | null>(null);
  const hasData = useRef(false);
  const elapsed = useRef(0);

  useEffect(() => {
    const tick = async () => {
      try {
        const d = (await superApi.health()) as HealthData;
        setData(d);
        hasData.current = true;
        lastOk.current = elapsed.current;
        setStaleSec(0);
        setFirstLoadFailed(false);
      } catch {
        if (!hasData.current) setFirstLoadFailed(true);
        else if (lastOk.current != null) setStaleSec(elapsed.current - lastOk.current);
      }
    };
    void tick();
    const id = window.setInterval(() => { elapsed.current += 5; void tick(); }, 5000);
    return () => window.clearInterval(id);
  }, []);

  if (firstLoadFailed && !data) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>
          <EmptyState
            icon="warn"
            title="אין חיבור לשרת הניטור"
            description="לא ניתן היה לקבל את מצב התשתית. ייתכן שיש תקלה — אל תניח שהכול תקין."
          />
        </Card>
      </div>
    );
  }
  if (!data?.asterisk || !data?.nodes) return <div style={{ padding: 'var(--gap)' }}><Card>טוען…</Card></div>;

  const lat = (ms?: number | null) => (ms == null ? '—' : `${ms}ms`);

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title="ניטור תשתית"
        actions={
          <StatusBadge tone={STATUS_TONE[data.status] ?? 'neutral'} dot>
            {STATUS_LABEL[data.status] ?? data.status}
          </StatusBadge>
        }
      />

      {staleSec >= 15 && (
        <div role="status" style={{ padding: '8px 12px', borderRadius: 'var(--r-2)', background: 'var(--warn-soft)', color: 'var(--text)', fontSize: 'var(--t-sm)' }}>
          ⚠ העדכון האחרון נכשל — הנתונים המוצגים בני {staleSec} שניות ואינם מעודכנים.
        </div>
      )}

      {/* Top-level vitals */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="שיחות פעילות" value={data.active_channels} />
        <Stat label="שיחות בשעה האחרונה" value={data.calls?.last_hour ?? 0} />
        <Stat label="שלוחות מחוברות" value={`${data.extensions?.registered ?? 0}/${data.extensions?.provisioned ?? 0}`} />
        <Stat label="טראנקים מחוברים" value={`${data.trunks?.online ?? 0}/${data.trunks?.total ?? 0}`} />
      </div>

      {/* Core services */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--gap)' }}>
        <Card title="Asterisk (טלפוניה)">
          <ServiceRow ok={data.asterisk.ok} latency={lat(data.asterisk.latency_ms)} />
        </Card>
        <Card title="מסד נתונים">
          <ServiceRow ok={data.database.ok} latency={lat(data.database.latency_ms)} extra={data.database.driver ?? undefined} />
          {data.database.error && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)', marginTop: 6 }} dir="ltr">{data.database.error}</div>}
        </Card>
      </div>

      {/* Call breakdown */}
      <Card title="שיחות (24 שעות אחרונות)">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--gap)' }}>
          <Stat label="נכנסות פעילות" value={data.calls?.inbound ?? 0} />
          <Stat label="יוצאות פעילות" value={data.calls?.outbound ?? 0} />
          <Stat label="זמן שיחה ממוצע" value={fmtDur(data.calls?.avg_talk_sec ?? 0)} />
        </div>
      </Card>

      {/* Trunks */}
      <Card title="טראנקים (קווי SIP)">
        {(!data.trunks?.items || data.trunks.items.length === 0) ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין טראנקים מוגדרים.</div>
        ) : (
          data.trunks.items.map((t) => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="globe" size={13} /> {t.name}
              </span>
              <StatusBadge tone={t.status === 'registered' ? 'ok' : t.status === 'failed' ? 'danger' : 'warn'} dot size="sm">
                {t.status === 'registered' ? 'רשום' : t.status === 'failed' ? 'נכשל' : t.status}
              </StatusBadge>
            </div>
          ))
        )}
      </Card>

      {/* Nodes */}
      <Card title="צמתים">
        {data.nodes.map((n) => (
          <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
            <div>
              <strong>{n.id}</strong>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">{n.role} · {n.host}</div>
            </div>
            <StatusBadge tone={n.status === 'healthy' ? 'ok' : n.status === 'down' ? 'danger' : 'warn'} dot size="sm">{n.status}</StatusBadge>
          </div>
        ))}
      </Card>

      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--faint)', textAlign: 'center' }}>
        נבדק לאחרונה: {new Date(data.checked_at).toLocaleString('he-IL')}
      </div>
    </div>
  );
}

function ServiceRow({ ok, latency, extra }: { ok: boolean; latency: string; extra?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <StatusBadge tone={ok ? 'ok' : 'danger'} dot>{ok ? 'תקין' : 'תקלה'}</StatusBadge>
      <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        {extra && <span dir="ltr">{extra}</span>}
        <span dir="ltr"><Icon name="bolt" size={12} /> {latency}</span>
      </span>
    </div>
  );
}

function fmtDur(s: number): string {
  if (!s) return '0:00';
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, '0');
  return `${m}:${r}`;
}
